import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import { useT, useContent, useProjects } from "@/i18n";

export default function Projects() {
  const t = useT();
  const { categories, references } = useContent();
  const { projects } = useProjects();
  const [params, setParams] = useSearchParams();
  const initial = params.get("kategori") || "all";
  const [active, setActive] = useState(initial);

  useEffect(() => {
    setActive(params.get("kategori") || "all");
  }, [params]);

  const counts = useMemo(() => {
    const c = { all: projects.length };
    projects.forEach((p) => (c[p.category] = (c[p.category] || 0) + 1));
    return c;
  }, [projects]);

  const filtered = useMemo(
    () => (active === "all" ? projects : projects.filter((p) => p.category === active)),
    [active, projects]
  );

  const setCat = (slug) => (slug === "all" ? setParams({}) : setParams({ kategori: slug }));

  return (
    <div data-testid="projects-page">
      <PageHeader
        title={t("projects.headerTitle")}
        crumb={t("nav.projects")}
        subtitle={t("projects.headerSubtitle")}
        image="/sections/hero-hospital.jpg"
      />

      {/* FILTERS — minimal text */}
      <section className="sticky top-[76px] z-30 bg-paper/90 backdrop-blur-xl border-b border-ink/10">
        <div className="container-kyt py-5 flex gap-7 overflow-x-auto no-scrollbar">
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setCat(c.slug)}
              data-testid={`filter-${c.slug}`}
              className={`group shrink-0 text-[13.5px] tracking-wide transition-colors duration-200 ${
                active === c.slug ? "text-ink" : "text-slate-400 hover:text-ink"
              }`}
            >
              {c.name}
              <span className="ml-1.5 font-mono text-[10px] align-super">{counts[c.slug] || 0}</span>
              <span className={`block h-px mt-1.5 bg-brand-blue origin-left transition-transform duration-300 ${active === c.slug ? "scale-x-100" : "scale-x-0"}`} />
            </button>
          ))}
        </div>
      </section>

      {/* GRID — gallery wall */}
      <section className="py-16 md:py-24 bg-paper">
        <div className="container-kyt">
          <div className="mb-12 font-mono text-[12px] tracking-wide uppercase text-slate-400" data-testid="projects-count">
            <span className="text-ink">{String(filtered.length).padStart(2, "0")}</span> {t("projects.countSuffix")}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 60}>
                <ProjectCard project={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DİĞER REFERANSLAR */}
      {active === "all" && (
        <section className="py-24 md:py-28 bg-white border-t border-ink/10">
          <div className="container-kyt">
            <div className="max-w-2xl">
              <div className="eyebrow mb-4">{t("projects.refEyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink">{t("projects.refTitle")}</h2>
              <p className="mt-4 text-slate-600 text-[15px] leading-relaxed font-light">
                {t("projects.refDesc")}
              </p>
            </div>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12">
              {references.map((r, i) => (
                <Reveal key={r.name} delay={(i % 3) * 50}>
                  <div className="group flex items-baseline gap-4 py-4 border-t border-ink/10" data-testid={`reference-${i}`}>
                    <span className="font-mono text-[11px] text-brand-blue shrink-0">—</span>
                    <span className="text-[14.5px] text-ink">{r.name}</span>
                    <span className="ml-auto font-mono text-[11px] text-slate-400">{r.loc}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
